import { groupBy, meanBy, minBy } from 'es-toolkit';

import { LumberGrade } from '@paragon/contract-library';
import { sortBy, throwIfNull } from '@paragon/utilities';

/**
 * Maintained serverside in LumberGradeRankings.BaseRankedGrades
 */
const baseRankedGrades: readonly LumberGrade[] = [
  'Economy',
  'Construction',
  'Utility',
  'Stud',
  'Standard',
  'Non_Dense_Number_3',
  'Number_3',
  'Number_3_Open_Grain',
  'Dense_Number_3',
  'Non_Dense_Number_2',
  'Number_2',
  'Number_2_Open_Grain',
  'Dense_Number_2',
  'Number_2_And_Better',
  'Number_1_And_2',
  'Non_Dense_Number_1',
  'Number_1',
  'Number_1_Open_Grain',
  'Number_1_And_Better',
  'Dense_Number_1',
  'Non_Dense_Select_Structural',
  'Select_Structural',
  'Select_Structural_Open_Grain',
  'Dense_Select_Structural',
  'Select_And_Better',
  'Clear_Structural',
  'MEL_M_5',
  'MEL_M_6',
  'MEL_M_7',
  'MEL_M_8',
  'MEL_M_9',
  'MEL_M_10',
  'MEL_M_11',
  'MEL_M_12',
  'MEL_M_13',
  'MEL_M_14',
  'MEL_M_15',
  'MEL_M_16',
  'MEL_M_17',
  'MEL_M_18',
  'MEL_M_19',
  'MEL_M_20',
  'MEL_M_21',
  'MEL_M_22',
  'MEL_M_23',
  'MEL_M_24',
  'MEL_M_25',
  'MEL_M_26',
  'MEL_M_27',
  'MEL_M_28',
  'MEL_M_29',
  'MEL_M_30',
  'MEL_M_31',
  'MEL_M_32',
  'MEL_M_33',
  'MEL_M_34',
  'MEL_M_35',
  'MEL_M_36',
  'MEL_M_37',
  'MEL_M_38',
  'MEL_M_39',
  'MEL_M_40',
  'MEL_M_41',
  'MEL_M_42',
  'MSR_750f_1_4E',
  'MSR_850f_1_4E',
  'MSR_900f_1_0E',
  'MSR_975f_1_6E',
  'MSR_1050f_1_2E',
  'MSR_1050f_1_6E',
  'MSR_1200f_1_2E',
  'MSR_1200f_1_3E',
  'MSR_1200f_1_6E',
  'MSR_1250f_1_4E',
  'MSR_1250f_1_6E',
  'MSR_1350f_1_3E',
  'MSR_1350f_1_4E',
  'MSR_1400f_1_2E',
  'MSR_1450f_1_3E',
  'MSR_1450f_1_5E',
  'MSR_1500f_1_4E',
  'MSR_1500f_1_5E',
  'MSR_1500f_1_6E',
  'MSR_1500f_1_7E',
  'MSR_1600f_1_4E',
  'MSR_1650f_1_3E',
  'MSR_1650f_1_5E',
  'MSR_1650f_1_6E',
  'MSR_1650f_1_7E',
  'MSR_1700f_1_6E',
  'LSL_1730F_1_35E',
  'MSR_1800f_1_5E',
  'MSR_1800f_1_6E',
  'MSR_1800f_1_8E',
  'MSR_1800f_2_0E',
  'MSR_1850f_1_7E',
  'MSR_1950f_1_5E',
  'MSR_1950f_1_7E',
  'MSR_2000f_1_6E',
  'MSR_2100f_1_8E',
  'MSR_2250f_1_7E',
  'MSR_2250f_1_8E',
  'MSR_2250f_1_9E',
  'LSL_2360F_1_55E',
  'MSR_2400f_1_8E',
  'MSR_2400f_2_0E',
  'LSL_2500F_1_75E',
  'MSR_2500f_2_2E',
  'MSR_2550f_1_8E',
  'MSR_2550f_2_1E',
  'MSR_2700f_2_0E',
  'MSR_2700f_2_2E',
  'MSR_2850f_1_8E',
  'MSR_2850f_2_3E',
  'MSR_3000f_2_4E',
  'LFL_1_6E',
  'LFL_1_7E',
  'LFL_1_9E',
  'LFL_2_1E',
  'Kerto_S_2_0E',
  'LVL_1_3E',
  'LVL_1_5E',
  'LVL_1_8E',
  'LVL_2_0E',
  'LVL_2_2E',
  'LVL_2_3E',
];

const tangibleGradeBaseRankMap = new Map<LumberGrade, number>(baseRankedGrades.map((grade, rank) => [grade, rank]));

function getTangibleGradeBaseRank(tangibleGrade: LumberGrade) {
  const tangibleGradeRank = tangibleGradeBaseRankMap.get(tangibleGrade);
  if (tangibleGradeRank == null) throw new Error(`Tangible grade ${tangibleGrade} not found in base rankings.`);
  return tangibleGradeRank;
}

/**
 * Maintained serverside in the LumberGradeRankings constructor
 */
export function buildRankingsIncludingComboGrades(
  comboGradesList: readonly (readonly LumberGrade[])[],
): readonly (readonly LumberGrade[])[] {
  const comboGradesByLowestTangibleGrade = groupBy(
    comboGradesList.map((comboGrades) => comboGrades.toSorted()),
    (comboGrade) => throwIfNull(minBy(comboGrade, getTangibleGradeBaseRank)),
  );

  return baseRankedGrades.flatMap((baseGrade) => [
    // Combo lumber is ranked just below its lowest tangible grade because it is preferable based on
    // its cost flexibility. If needed, rank it against other combo grades.
    ...sortBy(
      comboGradesByLowestTangibleGrade[baseGrade] ?? [],
      // A higher option count always ranks lower (better) than a lower option count if they share the
      // same lowest tangible grade.
      [(comboGrade) => comboGrade.length, (comboGrade) => meanBy(comboGrade, getTangibleGradeBaseRank)],
      ['desc', 'asc'],
    ),
    [baseGrade],
  ]);
}

/**
 * Maintained serverside in LumberGradeRankings.Get
 */
export function getGradeRank(
  lumberGrade: LumberGrade,
  tangibleGrades: readonly LumberGrade[],
  rankings: readonly (readonly LumberGrade[])[],
): number {
  // Compared as a whole sequence, as LumberGradeRankings.Get does with SequenceEqual. Comparing element by element
  // alone matches on a prefix, so a combo these rankings were not built with would quietly take the rank of a shorter
  // entry its grades happen to start with, usually the lone entry for its first grade. Better to say it is absent.
  const gradesToRank = lumberGrade === 'Combo' ? tangibleGrades.toSorted() : [lumberGrade];
  const index = rankings.findIndex(
    (rankedGradesEntry) =>
      rankedGradesEntry.length === gradesToRank.length &&
      rankedGradesEntry.every((grade, i) => grade === gradesToRank[i]),
  );
  if (index === -1) throw new Error(`${lumberGrade} not found in ranking`);
  return index;
}
